import { CheckIcon, MinusIcon } from '@chakra-ui/icons'
import {
  Badge,
  Button,
  Center,
  CircularProgress,
  Divider,
  Flex,
  FlexProps,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import {
  AssessmentStage,
  assessmentStageKeys,
  colors,
  getDateString,
  PracticeAssessmentStage,
  practiceAssessmentStageKeys,
} from '@hb/shared'
import { useContext, useMemo } from 'react'

import { useApp } from '../../../../../contexts/AppContext'
import { ProfileContext } from '../../../../../contexts/ProfileContext'
import { PregnancyArchiveButton } from './ArchiveButton'
import { usePregnancyStatus } from './hooks'
import { PregnancyStatusHistoryPopover } from './StatusHistory'

const AssessmentStatusButton = ({
  mode,
  stages,
  onClick,
  id,
  hovered,
  selectedIds,
  onHover,
  onBlur,
  isLoading,
}: {
  mode: 'add' | 'move' | null
  id: AssessmentStage | PracticeAssessmentStage
  stages?: Array<AssessmentStage | PracticeAssessmentStage>
  onClick: () => Promise<void>
  hovered: boolean
  selectedIds: Array<AssessmentStage | PracticeAssessmentStage>
  onHover: () => void
  onBlur: () => void
  isLoading: boolean
}) => {
  const { selected, active } = useMemo(
    () => ({
      selected: selectedIds?.includes(id),
      active: stages?.includes(id),
    }),
    [stages, selectedIds, id],
  )
  const pointerEvents = useMemo(() => {
    if (mode && active) return 'none'
    if (mode) return 'auto'
    return active ? 'auto' : 'none'
  }, [active, mode])

  return (
    <MenuItem
      zIndex={1}
      py={2}
      color="gray.600"
      pointerEvents={pointerEvents}
      onClick={onClick}
      closeOnSelect={false}
      fontWeight={600}
      background={hovered && (mode || active) ? 'gray.50' : 'white'}
      px={4}
      onPointerEnter={onHover}
      onPointerLeave={onBlur}>
      {id}
      <Center
        borderRadius={mode === 'move' ? 'full' : 4}
        ml="auto"
        bg={active && mode ? '#efefef' : 'transparent'}
        w={6}
        h={6}
        border={`1px solid ${mode ? '#cdcdcd' : 'transparent'}`}>
        {active || selected ? (
          <CheckIcon
            color={
              mode === 'move' && selectedIds.length && !selected ? 'gray.300' : colors.green.hex
            }
            display={hovered || isLoading ? 'none' : 'flex'}
            fontSize="0.8rem"
          />
        ) : null}
        {active || selected ? (
          <MinusIcon
            color="red.600"
            display={!hovered || isLoading ? 'none' : 'flex'}
            fontSize="0.8rem"
          />
        ) : null}
        {isLoading ? (
          <CircularProgress size="20px" color={colors.green.hex} isIndeterminate />
        ) : null}
      </Center>
    </MenuItem>
  )
}

export const PregnancyStatusDropdown = ({
  isMobile,
  ...props
}: FlexProps & { isMobile?: boolean }) => {
  const { selectedAssessment } = useContext(ProfileContext)
  const { appName } = useApp()
  const { practiceData, assessmentData, populated } = selectedAssessment ?? {}
  const stages = useMemo(
    () => (appName === 'app' ? populated?.adminStages : populated?.practiceStages),
    [appName, populated],
  )
  const stageHistory = useMemo(
    () => (appName === 'app' ? populated?.adminStageHistory : populated?.practiceStageHistory),
    [appName, populated],
  )

  const disclosure = useDisclosure()

  const { isOpen, onClose, onOpen } = disclosure
  const {
    hovered,
    setHovered,
    mode,
    setMode,
    selectedIds,
    loadingIds,
    handleCancel,
    handleConfirm,
    handleItemClick,
    setMoveMode,
  } = usePregnancyStatus(disclosure)

  const archivedOn = useMemo(
    () => (appName === 'providers-app' ? practiceData?.archivedOn : assessmentData?.archivedOn),
    [appName, assessmentData, practiceData],
  )
  return (
    <Flex gap={2} align="center" {...props}>
      {/* <i>[Status Dropdown]</i> */}
      {archivedOn ? (
        <Badge colorScheme="red" fontSize="0.85rem">
          {isMobile ? '' : 'ARCHIVED '}
          {getDateString(archivedOn, 'short')}
        </Badge>
      ) : null}
      <Menu isOpen={isOpen} onClose={onClose} onOpen={onOpen} closeOnBlur={false} strategy="fixed">
        <MenuButton
          as={Button}
          variant="outline"
          color="blackAlpha.700"
          bg="white"
          px={4}
          py={1}
          borderRadius="full"
          // boxShadow='0 0 4px rgba(0,0,0.5)'
          shadow="md">
          <Flex align="center" justify="center">
            <Text fontWeight={500} fontFamily="Hero-New" fontSize="sm" lineHeight={1}>
              {stages?.join(', ') || 'Not on any lists'}
            </Text>
          </Flex>
        </MenuButton>
        <Portal>
          <MenuList border="1px solid #cdcdcd" boxShadow="md" w="350px" py={0}>
            <VStack w="100%" divider={<Divider />} spacing={0}>
              <HStack w="100%" py={2} bg="gray.100" px={2} justify="space-between">
                {mode ? (
                  <>
                    <Button
                      isDisabled={!!loadingIds.length}
                      onClick={handleCancel}
                      bg="white"
                      size="sm"
                      variant="outline">
                      Cancel
                    </Button>
                    <Button
                      isLoading={!!loadingIds.length}
                      onClick={handleConfirm}
                      bg={colors.green.hex}
                      color="white"
                      size="sm"
                      variant="outline">
                      Save
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => setMode('add')}
                      bg={colors.green.hex}
                      size="sm"
                      color="white"
                      borderRadius="full">
                      Add to list(s)
                    </Button>
                    <PregnancyStatusHistoryPopover stageHistory={stageHistory} />
                    <Button
                      bg={colors.green.hex}
                      onClick={setMoveMode}
                      color="white"
                      size="sm"
                      borderRadius="full">
                      Move to list
                    </Button>
                  </>
                )}
              </HStack>
              {(appName === 'app' ? assessmentStageKeys : practiceAssessmentStageKeys).map(k => (
                <AssessmentStatusButton
                  key={k}
                  id={k}
                  onBlur={() => setHovered(null)}
                  mode={mode}
                  hovered={hovered === k}
                  selectedIds={selectedIds}
                  isLoading={loadingIds.includes(k)}
                  onClick={() => handleItemClick(k)}
                  onHover={() => setHovered(k)}
                  stages={stages}
                />
              ))}
              <PregnancyArchiveButton />
              {appName === 'app' && practiceData?.archivedOn ? (
                <MenuItem
                  fontSize="xs"
                  fontWeight={500}
                  closeOnSelect={false}
                  px={3}
                  py={1}
                  bg="gray.50"
                  color={'red.600'}>
                  {`ARCHIVED ON PRACTICE ${getDateString(practiceData.archivedOn, 'short')}`}
                </MenuItem>
              ) : null}
            </VStack>
          </MenuList>
        </Portal>
      </Menu>
    </Flex>
  )
}
